import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { 
  fetchMentorAvailability, 
  MentorAvailabilitySlot,
  getAvailableDatesForMentorWithSessions,
  getAvailableTimeSlotsForDate
} from "@/services/mentorAvailabilityService";

export type TimeSlot = {
  start: string;
  end: string;
  available: boolean;
};

export function useMentorScheduleAvailability(mentorId: string, selectedDate?: Date | undefined) {
  const [availabilitySlots, setAvailabilitySlots] = useState<MentorAvailabilitySlot[]>([]);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Loading inicial (slots)
  const [isDatesLoading, setIsDatesLoading] = useState<boolean>(false); // Loading das datas
  const [error, setError] = useState<Error | null>(null);
  const [scheduledSessions, setScheduledSessions] = useState<any[]>([]); // Cache das sessões

  // Fetch mentor availability patterns - PROGRESSIVE LOADING
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // 🎯 FASE 1: Carregar apenas os slots primeiro (rápido - modal pode abrir)
        const slots = await fetchMentorAvailability(mentorId);
        setAvailabilitySlots(slots);
        setIsLoading(false); // Modal abre aqui!
        
        // 🎯 FASE 2: Carregar datas disponíveis em background
        setIsDatesLoading(true);
        const dates = await getAvailableDatesForMentorWithSessions(mentorId, slots);
        setAvailableDates(dates);
        setIsDatesLoading(false);
        
      } catch (err) {
        console.error("Error fetching mentor availability:", err);
        setError(err instanceof Error ? err : new Error('Failed to fetch availability'));
        setIsLoading(false);
        setIsDatesLoading(false);
      }
    };

    if (mentorId) {
      fetchAvailability();
    }
  }, [mentorId]);

  // When selectedDate changes, get available time slots for that date (considering booked sessions)
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (selectedDate && availabilitySlots.length > 0 && mentorId) {
        try {
          // 🎯 OTIMIZAÇÃO: Usar cache de sessões se disponível
          let cachedSessions = scheduledSessions;
          if (cachedSessions.length === 0) {
            // Se não temos cache, buscar e armazenar
            const { fetchMentorScheduledSessions } = await import("@/services/mentorAvailabilityService");
            cachedSessions = await fetchMentorScheduledSessions(mentorId);
            setScheduledSessions(cachedSessions);
          }
          
          // Get available time slots for the selected date (usando cache)
          const slots = await getAvailableTimeSlotsForDate(selectedDate, mentorId, availabilitySlots, cachedSessions);
          
          // Convert to TimeSlot format
          const timeSlots = slots.map(slot => ({
            start: slot.startTime,
            end: slot.endTime,
            available: true
          }));
          
          setAvailableTimeSlots(timeSlots);
        } catch (err) {
          console.error("Error fetching available time slots:", err);
          setAvailableTimeSlots([]);
        }
      } else {
        setAvailableTimeSlots([]);
      }
    };

    fetchTimeSlots();
  }, [selectedDate, availabilitySlots, mentorId, scheduledSessions]);

  // Function to check if a date is in the available dates array
  const isDateAvailable = (date: Date) => {
    // Verificação de segurança - se não temos datas disponíveis ainda, consideramos como não disponível
    if (!availableDates || availableDates.length === 0) {
      return false;
    }
    
    // Verificação adicional se date é válido
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('[useMentorScheduleAvailability] Invalid date provided to isDateAvailable:', date);
      return false;
    }
    
    return availableDates.some(availableDate => {
      // Verificação adicional se availableDate é válido
      if (!availableDate || !(availableDate instanceof Date) || isNaN(availableDate.getTime())) {
        console.warn('[useMentorScheduleAvailability] Invalid available date found:', availableDate);
        return false;
      }
      
      return availableDate.getDate() === date.getDate() && 
             availableDate.getMonth() === date.getMonth() &&
             availableDate.getFullYear() === date.getFullYear();
    });
  };
  
  return { 
    availabilitySlots,
    availableDates, 
    availableTimeSlots, 
    isLoading, // Loading inicial (slots) - modal abre quando false
    isDatesLoading, // Loading das datas disponíveis
    error,
    isDateAvailable
  };
}
